import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { App as AntdApp, Table, Input, Select, Space, Button, Tag, Tooltip, Typography } from 'antd'
import { CopyOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons'
import { useMessageList, useMarkResponded, useCloseNoTicket } from '@/modules/message/queries'
import { useGenerateDraft } from '@/modules/draft/queries'
import { useAuthStore } from '@/stores/authStore'
import StatusTag from '@/components/feedback/StatusTag'
import ErrorResult from '@/components/feedback/ErrorResult'
import { formatMessageTime } from '@/utils/datetime'
import type { MessageItem } from '@/types/message'
import MessageDetailDrawer from './components/MessageDetailDrawer'
import ManualEntryModal from './components/ManualEntryModal'
import { canGenerateDraftFromMessage } from './messageDraftRules'

const { Text } = Typography
const CONTENT_TYPE_LABELS: Record<string, string> = {
  text: '文本',
  image: '图片',
  file: '文档',
}

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : '操作失败'
}

async function copyText(text: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text)
    return
  }

  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.setAttribute('readonly', '')
  textarea.style.position = 'fixed'
  textarea.style.top = '-9999px'
  document.body.appendChild(textarea)
  textarea.select()
  const copied = document.execCommand('copy')
  textarea.remove()
  if (!copied) throw new Error('copy failed')
}

export default function MessageListPage() {
  const { message } = AntdApp.useApp()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [keyword, setKeyword] = useState('')
  const [statusFilter, setStatusFilter] = useState<string | undefined>()
  const [bindingStatusFilter, setBindingStatusFilter] = useState<string | undefined>()
  const [sortValue, setSortValue] = useState('created_at.desc')
  const [detailId, setDetailId] = useState<string | null>(null)
  const [manualModalOpen, setManualModalOpen] = useState(false)
  const [pendingAction, setPendingAction] = useState<{ type: 'respond' | 'draft' | 'close'; messageId: string } | null>(null)

  const { data, isLoading, isError, error, refetch } = useMessageList({
    page,
    page_size: pageSize,
    keyword: keyword || undefined,
    message_status: statusFilter,
    binding_status: bindingStatusFilter,
    sort_by: sortValue.split('.')[0],
    sort_order: sortValue.split('.')[1],
  })

  const hasPermission = useAuthStore((s) => s.hasPermission)

  const markResponded = useMarkResponded()
  const closeNoTicket = useCloseNoTicket()
  const generateDraft = useGenerateDraft()

  useEffect(() => {
    setDetailId(searchParams.get('messageId'))
  }, [searchParams])

  function openMessageDetail(messageId: string) {
    const next = new URLSearchParams(searchParams)
    next.set('messageId', messageId)
    setSearchParams(next, { replace: true })
  }

  function closeMessageDetail() {
    const next = new URLSearchParams(searchParams)
    next.delete('messageId')
    setSearchParams(next, { replace: true })
  }

  async function copyRoomId(roomId: string) {
    const normalizedRoomId = roomId.trim()
    if (!normalizedRoomId) {
      message.warning('当前消息没有群ID')
      return
    }
    try {
      await copyText(normalizedRoomId)
      message.success('群ID已复制')
    } catch {
      message.error('复制群ID失败')
    }
  }

  function renderRoomName(value: string, row: MessageItem) {
    const displayName = value || row.room_id
    if (!displayName) return <Text type="secondary">未识别</Text>

    if (!row.room_id) {
      return displayName
    }

    const copy = () => { void copyRoomId(row.room_id) }
    const roomNameTooltip = <span data-testid="message-room-name-tooltip">{displayName}</span>
    const roomIdTooltip = <span data-testid="message-room-id-tooltip">{row.room_id}</span>
    const copyTooltip = <span data-testid="message-room-id-copy-tooltip">复制群ID</span>
    return (
      <Space orientation="vertical" size={0} style={{ maxWidth: '100%' }}>
        <Space size={4} wrap={false} style={{ maxWidth: '100%' }}>
          <Tooltip title={roomNameTooltip}>
            <Text
              ellipsis
              style={{ maxWidth: 192 }}
              data-testid="message-room-id-copy-text"
            >
              {displayName}
            </Text>
          </Tooltip>
          <Tooltip title={copyTooltip}>
            <Button
              aria-label="复制群ID"
              data-testid="message-room-id-copy-button"
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={copy}
              style={{ paddingInline: 4 }}
            />
          </Tooltip>
        </Space>
        {row.room_id !== displayName && (
          <Tooltip title={roomIdTooltip}>
            <Text
              type="secondary"
              style={{ fontSize: 12 }}
              ellipsis
              data-testid="message-room-id-copy-value"
            >
              {row.room_id}
            </Text>
          </Tooltip>
        )}
      </Space>
    )
  }

  if (isError) return <ErrorResult error={error} onRetry={() => refetch()} />

  return (
    <div data-testid="messages-page" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* 筛选栏 */}
      <Space wrap>
        <span data-testid="messages-search-input">
          <Input
            placeholder="搜索客户/群名/正文关键字"
            prefix={<SearchOutlined />}
            allowClear
            style={{ width: 280 }}
            value={keyword}
            onChange={(e) => { setKeyword(e.target.value); setPage(1) }}
          />
        </span>
        <Select
          placeholder="消息状态"
          allowClear
          style={{ width: 140 }}
          value={statusFilter}
          onChange={(v) => { setStatusFilter(v); setPage(1) }}
          options={[
            { label: '待响应', value: 'pending_response' },
            { label: '已响应', value: 'responded' },
            { label: '已转草稿', value: 'drafted' },
            { label: '无需建单', value: 'closed_no_ticket' },
            { label: '待补绑定', value: 'binding_required' },
          ]}
        />
        <Select
          placeholder="绑定状态"
          allowClear
          style={{ width: 120 }}
          value={bindingStatusFilter}
          onChange={(v) => { setBindingStatusFilter(v); setPage(1) }}
          options={[
            { label: '已绑定', value: 'matched' },
            { label: '未绑定', value: 'unmatched' },
          ]}
        />
        <Select
          aria-label="排序"
          style={{ width: 180 }}
          value={sortValue}
          onChange={(v) => { setSortValue(v); setPage(1) }}
          options={[
            { label: '最新入库在前', value: 'created_at.desc' },
            { label: '最早入库在前', value: 'created_at.asc' },
            { label: '最新消息在前', value: 'send_time.desc' },
            { label: '最早消息在前', value: 'send_time.asc' },
            { label: '最近更新在前', value: 'updated_at.desc' },
          ]}
        />
        {hasPermission('message:create') && (
          <Button data-testid="message-action-manual-entry" type="primary" icon={<PlusOutlined />} onClick={() => setManualModalOpen(true)}>人工录入</Button>
        )}
      </Space>

      {/* 表格 */}
      <Table<MessageItem>
        size="small"
        rowKey="message_id"
        loading={isLoading}
        dataSource={data?.items || []}
        pagination={{
          current: page,
          pageSize,
          total: data?.total || 0,
          showSizeChanger: true,
          showTotal: (t) => `共 ${t} 条`,
          onChange: (p, ps) => { setPage(p); setPageSize(ps) },
        }}
        columns={[
          { title: '消息时间', dataIndex: 'send_time', width: 120, render: (v: string) => formatMessageTime(v) },
          { title: '入库时间', dataIndex: 'ingested_at', width: 120, render: (v: string) => formatMessageTime(v) },
          {
            title: '群名',
            dataIndex: 'room_name',
            width: 240,
            ellipsis: true,
            render: renderRoomName,
          },
          { title: '客户', dataIndex: 'customer_name', width: 100, render: (v: string) => v || <Text type="secondary">未绑定</Text> },
          { title: '发送人', dataIndex: 'sender_name', width: 80 },
          { title: '摘要', dataIndex: 'content_preview', ellipsis: true },
          {
            title: '类型', dataIndex: 'content_type', width: 70,
            render: (v: string) => <Tag>{CONTENT_TYPE_LABELS[v] || v || '文本'}</Tag>,
          },
          {
            title: '绑定', dataIndex: 'binding_status', width: 80,
            render: (v: string) => v === 'matched' ? <Tag color="green">已绑定</Tag> : <Tag color="red">未绑定</Tag>,
          },
          { title: '状态', dataIndex: 'message_status', width: 100, render: (v: string) => <StatusTag type="message" status={v} /> },
          {
            title: '操作', width: 180, render: (_: unknown, r: MessageItem) => (
              <Space size="small">
                <Button data-testid="message-action-view" type="link" size="small" onClick={() => openMessageDetail(r.message_id)}>查看</Button>
                {r.operator_actions?.includes('mark_responded') && hasPermission('message:respond') && (
                  <Button data-testid="message-action-respond" type="link" size="small"
                    onClick={() => {
                      setPendingAction({ type: 'respond', messageId: r.message_id })
                      markResponded.mutate(
                        { messageId: r.message_id },
                        {
                          onSuccess: () => message.success('已标记响应'),
                          onSettled: () => setPendingAction(null),
                        },
                      )
                    }}
                    loading={pendingAction?.type === 'respond' && pendingAction.messageId === r.message_id}>
                    标记响应
                  </Button>
                )}
                {canGenerateDraftFromMessage(r.message_status, r.operator_actions) && hasPermission('draft:update') && (
                  <Button
                    data-testid="message-action-generate-draft"
                    type="link"
                    size="small"
                    loading={pendingAction?.type === 'draft' && pendingAction.messageId === r.message_id}
                    onClick={async () => {
                      setPendingAction({ type: 'draft', messageId: r.message_id })
                      const messageKey = `generate-draft-${r.message_id}`
                      message.loading({ content: '正在生成草稿，请稍候...', key: messageKey, duration: 0 })
                      try {
                        const result = await generateDraft.mutateAsync(r.message_id)
                        if (!result.id) throw new Error('后端未返回草稿ID')
                        message.success({ content: '草稿生成成功，正在打开...', key: messageKey })
                        navigate(`/drafts/${result.id}`)
                      } catch (error) {
                        message.error({ content: `草稿生成失败：${errorMessage(error)}`, key: messageKey, duration: 5 })
                      } finally {
                        setPendingAction(null)
                      }
                    }}
                  >
                    生成草稿
                  </Button>
                )}
                {r.operator_actions?.includes('close_no_ticket') && hasPermission('message:respond') && (
                  <Button data-testid="message-action-close-no-ticket" type="link" size="small" danger
                    loading={pendingAction?.type === 'close' && pendingAction.messageId === r.message_id}
                    onClick={() => {
                      setPendingAction({ type: 'close', messageId: r.message_id })
                      closeNoTicket.mutate(
                        { messageId: r.message_id, reason: '简单咨询无需建单' },
                        {
                          onSuccess: () => message.success('已关闭为无需建单'),
                          onSettled: () => setPendingAction(null),
                        },
                      )
                    }}>
                    无需建单
                  </Button>
                )}
              </Space>
            ),
          },
        ]}
      />

      {/* 详情抽屉 */}
      <MessageDetailDrawer
        messageId={detailId}
        onClose={closeMessageDetail}
      />

      {/* 人工录入弹窗 */}
      <ManualEntryModal
        open={manualModalOpen}
        onClose={() => setManualModalOpen(false)}
      />
    </div>
  )
}
